/**
 * Netly Pay data for a screen: is this shop linked, and what did the API say.
 *
 * Every Netly Pay screen begins with the same question, and `not_linked` is an
 * answer rather than a failure — it means show the Enable screen (D54). These
 * hooks keep that branch in one place so no screen renders an error for a shop
 * that simply has not set Netly Pay up.
 */
import {useCallback, useEffect, useState} from 'react';
import NetlyPayRepository, {
  isNotLinked,
  type LinkState,
  type Workspace,
} from '../../../internals/repository/NetlyPayRepository';

export type LinkPhase = 'loading' | 'linked' | 'not_linked' | 'error';

export interface UseLink {
  phase: LinkPhase;
  workspace: Workspace | null;
  role: 'OWNER' | 'STAFF' | null;
  error: string | null;
  reload: () => void;
}

export function useNetlyPayLink(shopId?: string | null): UseLink {
  const [phase, setPhase] = useState<LinkPhase>('loading');
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [role, setRole] = useState<'OWNER' | 'STAFF' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!shopId) return;
    let live = true;
    setPhase('loading');
    setError(null);

    NetlyPayRepository.getLink(shopId)
      .then((res: LinkState) => {
        if (!live) return;
        if (res?.linked) {
          setWorkspace(res.workspace);
          setRole(res.role);
          setPhase('linked');
        } else {
          setWorkspace(null);
          setRole(null);
          setPhase('not_linked');
        }
      })
      .catch((e) => {
        if (!live) return;
        // A 409 reaches here as an error, but it is the unlinked state.
        if (isNotLinked(e)) {
          setPhase('not_linked');
          return;
        }
        setError(e?.message ?? 'Could not reach Netly Pay');
        setPhase('error');
      });

    return () => {
      live = false;
    };
  }, [shopId, tick]);

  return {phase, workspace, role, error, reload};
}

export type FetchPhase = 'loading' | 'ready' | 'empty' | 'error';

export interface UseFetch<T> {
  phase: FetchPhase;
  data: T | null;
  error: string | null;
  reload: () => void;
}

/**
 * One fetch, with the four table states the design previews: loading, data,
 * empty and error.
 *
 * `isEmpty` decides which of data and empty applies, because "no members yet"
 * and "no members match this filter" are different screens and only the caller
 * knows which list it is looking at.
 */
export function useNetlyPayFetch<T>(
  shopId: string | null | undefined,
  fetcher: (shopId: string) => Promise<T>,
  deps: any[] = [],
  isEmpty?: (data: T) => boolean,
): UseFetch<T> {
  const [phase, setPhase] = useState<FetchPhase>('loading');
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!shopId) return;
    let live = true;
    setPhase('loading');
    setError(null);

    fetcher(shopId)
      .then((res) => {
        if (!live) return;
        setData(res);
        setPhase(isEmpty && isEmpty(res) ? 'empty' : 'ready');
      })
      .catch((e) => {
        if (!live) return;
        setError(e?.message ?? 'Could not reach Netly Pay');
        setPhase('error');
      });

    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, tick, ...deps]);

  return {phase, data, error, reload};
}
